import React, { createContext, useContext, useEffect } from 'react';
import {
  useGetMeQuery,
  useGetOnlineStatusQuery,
  ListenToHeartbeatDocument,
  useCreateOnlineHeartbeatMutation
} from 'generated/schemaTypes';

const TIMEOUT = 1000 * 60 * 10;

type OnlineStatusContextType = { online: boolean; loading: boolean };

const OnlineStatusContext = createContext<OnlineStatusContextType>({
  online: false,
  loading: true
});

const OnlineStatusProvider: React.FC = ({ children }) => {
  const { data: identityData } = useGetMeQuery();
  const userID = identityData?.me?.id ?? '';

  const { loading, data, subscribeToMore } = useGetOnlineStatusQuery();

  const online = data?.getOnlineStatus?.online ?? false;

  const [heartbeatMutation] = useCreateOnlineHeartbeatMutation();

  useEffect(() => {
    if (!userID) {
      return;
    }
    subscribeToMore({
      document: ListenToHeartbeatDocument,
      variables: { userID }
    });
  }, [userID, subscribeToMore]);

  useEffect(() => {
    if (loading) return;
    const createHeartbeat = () => {
      if (!userID) return;
      process.env.NODE_ENV === 'production'
        ? heartbeatMutation({ variables: { userID } })
        : console.log('temp heartbeat func');
    };

    // heartbeat on mount / user change
    if (userID && online) createHeartbeat();

    // heartbeat every 5 minutes
    const interval = setInterval(() => {
      if (userID && online) createHeartbeat();
    }, TIMEOUT);

    return () => clearInterval(interval);
  }, [loading, userID, online, heartbeatMutation]);

  return (
    <OnlineStatusContext.Provider
      value={{
        online,
        loading
      }}
    >
      {children}
    </OnlineStatusContext.Provider>
  );
};

const useOnlineStatus = () => useContext(OnlineStatusContext);

export { OnlineStatusProvider, useOnlineStatus };
export default OnlineStatusProvider;
